import React from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { AlertTriangle, Monitor, XCircle } from "lucide-react";
import { MonitorDetectionResult } from "@/lib/monitorDetection";
import { MonitorDetectionAction } from "@/types/video-call";

interface MonitoringViolationDialogProps {
  showWarning: boolean;
  showBlock: boolean;
  monitorResult: MonitorDetectionResult | null;
  action: MonitorDetectionAction;
  onDismissWarning: () => void;
  onDismissBlock: () => void;
  onRetry?: () => void;
}

export const MonitoringViolationDialog: React.FC<MonitoringViolationDialogProps> = ({
  showWarning,
  showBlock,
  monitorResult,
  action,
  onDismissWarning,
  onDismissBlock,
  onRetry,
}) => {
  // Warning Dialog
  if (showWarning && action === "warning") {
    return (
      <Dialog open={showWarning} onOpenChange={onDismissWarning}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center">
              <AlertTriangle className="w-6 h-6 mr-2 text-mid" />
              Multiple Monitors Detected
            </DialogTitle>
            <DialogDescription>
              We've detected multiple displays connected to your system.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {monitorResult && (
              <div className="bg-mid-bg-mid-bg/20 border border-mid-line rounded-lg p-3">
                <div className="space-y-2">
                  <p className="text-sm font-medium text-mid">
                    Detection Details:
                  </p>
                  <ul className="text-xs text-mid space-y-1">
                    <li>• {monitorResult.screenCount} displays detected</li>
                    <li>• Detection method: {monitorResult.detectionMethod}</li>
                    <li>• Confidence: {Math.round(monitorResult.confidence * 100)}%</li>
                  </ul>
                </div>
              </div>
            )}

            <div className="text-sm text-muted-foreground dark:text-ink-faint">
              <p className="mb-2">For proctoring compliance, please:</p>
              <ul className="list-disc list-inside space-y-1">
                <li>Disconnect all external monitors</li>
                <li>Turn off screen mirroring</li>
                <li>Disable extended desktop mode</li>
                <li>Close virtual display software</li>
              </ul>
            </div>
          </div>

          <DialogFooter>
            <Button onClick={onDismissWarning}>
              I'll Continue Anyway
            </Button>
            {onRetry && (
              <Button variant="default" onClick={onRetry}>
                Check Again
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  // Block Dialog
  if (showBlock && action === "block") {
    return (
      <Dialog open={showBlock} onOpenChange={onDismissBlock}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center">
              <XCircle className="w-6 h-6 mr-2 text-danger" />
              Access Blocked
            </DialogTitle>
            <DialogDescription>
              Multiple monitors detected. Please resolve this before continuing.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {monitorResult && (
              <div className="bg-danger-bg-danger-bg/20 border border-danger-line rounded-lg p-3">
                <div className="space-y-2">
                  <p className="text-sm font-medium text-danger">
                    Security Violation Details:
                  </p>
                  <ul className="text-xs text-danger space-y-1">
                    <li>• {monitorResult.screenCount} displays detected</li>
                  </ul>
                </div>
              </div>
            )}

            <div className="text-sm text-muted-foreground dark:text-ink-faint">
              <p className="mb-2 font-medium">Required Actions:</p>
              <ul className="list-disc list-inside space-y-1">
                <li>Physically disconnect all external monitors</li>
                <li>Unplug HDMI, USB-C, and wireless display adapters</li>
                <li>Close virtual display software (TeamViewer, etc.)</li>
                <li>Disable screen mirroring and extended desktop</li>
                <li>Restart your computer if necessary</li>
              </ul>
            </div>

            <div className="bg-secondary/20 border border-border-border rounded-lg p-3">
              <div className="flex items-start">
                <Monitor className="w-4 h-4 text-foreground mt-0.5 mr-2 flex-shrink-0" />
                <div>
                  <p className="text-xs font-medium text-foreground">
                    Single Monitor Only
                  </p>
                  <p className="text-xs text-foreground mt-1">
                    Proctoring requires a single display setup. Once you've disconnected all external 
                    displays, click "Check Again" to verify compliance.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={onDismissBlock}>
              Cancel Session
            </Button>
            {onRetry && (
              <Button onClick={onRetry}>
                Check Again
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return null;
};